/**
 * Single source of truth for AI platform configuration.
 *
 * No feature code may hardcode a provider name, model id, timeout, or retry
 * count. Everything is read from here (and, where relevant, overridden by
 * build-time environment variables).
 */

import { isFeatureEnabled } from '@/lib/featureFlags';

import type { AICapability, AIProviderName } from './types';

export interface AIConfig {
  /** Provider used for every operation unless a request overrides it. */
  activeProvider: AIProviderName;
  /**
   * Provider tried when the active provider fails with a retryable error.
   * Not wired up yet — reserved for a later phase.
   */
  fallbackProvider: AIProviderName | null;
  /**
   * Optional per-feature provider assignment. When a capability is listed here
   * the provider manager prefers it over `activeProvider`. Empty by default, so
   * routing is unchanged until an entry is added.
   */
  capabilityProviders?: Partial<Record<AICapability, AIProviderName>>;
  /** Default model per capability, keyed by provider. */
  defaultModels: Partial<Record<AIProviderName, Partial<Record<AICapability, string>>>>;
  /** Request timeout in milliseconds. */
  timeoutMs: number;
  /** Number of retries after the initial attempt, for retryable errors only. */
  retryCount: number;
  /** Base delay for exponential backoff between retries, in milliseconds. */
  retryBaseDelayMs: number;
  /** Emit structured console logs for every AI call. */
  debug: boolean;
  /**
   * Observability layer. Disabled by default: until a real provider is
   * registered (Phase 1D) telemetry would only measure no-ops, so it stays
   * behind a flag and costs one boolean check per request.
   */
  telemetry: TelemetryConfig;
}

export interface TelemetryConfig {
  /** Master switch. Off unless `ai_telemetry` / `VITE_AI_TELEMETRY` is on. */
  enabled: boolean;
  /** Attach the structured console sink when telemetry is enabled. */
  consoleSink: boolean;
  /** Retained latency samples per metrics bucket. */
  maxLatencySamples: number;
}

const env = (import.meta.env ?? {}) as Record<string, string | undefined>;

const readProvider = (value: string | undefined, fallback: AIProviderName): AIProviderName => {
  const allowed: AIProviderName[] = ['lovable', 'openai', 'anthropic', 'google', 'mock'];
  return allowed.includes(value as AIProviderName) ? (value as AIProviderName) : fallback;
};

const readInt = (value: string | undefined, fallback: number): number => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const aiConfig: AIConfig = {
  activeProvider: readProvider(env.VITE_AI_PROVIDER, 'openai'),
  fallbackProvider: env.VITE_AI_FALLBACK_PROVIDER
    ? readProvider(env.VITE_AI_FALLBACK_PROVIDER, 'lovable')
    : null,
  // Per-feature overrides, e.g. { moderation: 'openai' }. Empty = use activeProvider.
  capabilityProviders: {},
  defaultModels: {
    // Phase 2A — OpenAI is the first production provider. Every model id is
    // overridable per environment so staging can trial a different tier.
    openai: {
      chat: env.VITE_AI_OPENAI_CHAT_MODEL || 'gpt-4o-mini',
      'text-improvement': env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      'event-description': env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      'event-title': env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      translation: env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      marketing: env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      pricing: env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      forecast: env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      insights: env.VITE_AI_OPENAI_TEXT_MODEL || 'gpt-4o-mini',
      moderation: env.VITE_AI_OPENAI_MODERATION_MODEL || 'gpt-4o-mini',
    },
    // Documents what production runs today. Phase 1B adds an `openai` entry.
    lovable: {
      chat: 'google/gemini-3-flash-preview',
      'text-improvement': 'google/gemini-2.5-flash',
      'event-description': 'google/gemini-2.5-flash',
      'event-title': 'google/gemini-2.5-flash',
      translation: 'google/gemini-2.5-flash',
      marketing: 'google/gemini-3-flash-preview',
      pricing: 'google/gemini-3-flash-preview',
      forecast: 'google/gemini-3-flash-preview',
      insights: 'google/gemini-3-flash-preview',
      moderation: 'google/gemini-3-flash-preview',
      'image-generation': 'google/gemini-2.5-flash-image',
    },
  },
  timeoutMs: readInt(env.VITE_AI_TIMEOUT_MS, 30_000),
  retryCount: readInt(env.VITE_AI_RETRY_COUNT, 2),
  retryBaseDelayMs: readInt(env.VITE_AI_RETRY_BASE_DELAY_MS, 500),
  debug: env.VITE_AI_DEBUG === 'true',
  telemetry: {
    // Feature-flag driven, so staging can switch it on without a code change.
    enabled: env.VITE_AI_TELEMETRY === 'true' || isFeatureEnabled('ai_telemetry'),
    consoleSink: env.VITE_AI_TELEMETRY_CONSOLE === 'true' || env.VITE_AI_DEBUG === 'true',
    maxLatencySamples: readInt(env.VITE_AI_TELEMETRY_SAMPLES, 500),
  },
};

/** Resolve the model for a capability, honouring an explicit override. */
export function resolveModel(
  capability: AICapability,
  provider: AIProviderName = aiConfig.activeProvider,
  override?: string,
): string | undefined {
  return override ?? aiConfig.defaultModels[provider]?.[capability];
}
